import React, { useMemo, useState, createElement } from 'react';
import { Download, Copy, Check } from 'lucide-react';
import { AppState, GroupId } from '../../../types';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
interface StateExplorerProps {
  state: AppState;
  selectedGroup: GroupId;
}
export function StateExplorer({
  state,
  selectedGroup
}: StateExplorerProps) {
  const [copied, setCopied] = useState(false);
  const [activeView, setActiveView] = useState<'stats' | 'progress' | 'questions' | 'family'>('stats');
  const stateStats = useMemo(() => {
    return {
      totalXP: state.stats.xp,
      level: state.stats.level,
      totalQuestions: Object.keys(state.questionStates).length,
      masteredQuestions: Object.values(state.questionStates).filter(q => q.status === 'mastered').length,
      familyMasteryCount: state.familyMastery ? Object.keys(state.familyMastery).length : 0,
      placementTests: state.placementResults ? Object.keys(state.placementResults).length : 0
    };
  }, [state]);
  const handleExportState = () => {
    const dataStr = JSON.stringify(state, null, 2);
    const dataBlob = new Blob([dataStr], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `meesterjay-state-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const handleCopyState = () => {
    navigator.clipboard.writeText(JSON.stringify(state, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="space-y-6" data-id="element-1907">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1908">
        <StatBox label="XP" value={stateStats.totalXP.toLocaleString()} color="amber" data-id="element-1909" />

        <StatBox label="Level" value={stateStats.level} color="indigo" data-id="element-1910" />
        <StatBox label="Questions" value={stateStats.totalQuestions} color="blue" data-id="element-1911" />

        <StatBox label="Mastered" value={stateStats.masteredQuestions} color="emerald" data-id="element-1912" />

        <StatBox label="Families" value={stateStats.familyMasteryCount} color="purple" data-id="element-1913" />

        <StatBox label="Placements" value={stateStats.placementTests} color="rose" data-id="element-1914" />

      </div>

      {/* Actions */}
      <Card className="p-4 border-slate-200 shadow-sm" data-id="element-1915">
        <div className="flex flex-wrap gap-3" data-id="element-1916">
          <Button onClick={handleExportState} variant="secondary" size="sm" data-id="element-1917">
            <Download className="w-4 h-4 mr-2" data-id="element-1918" />
            Export State
          </Button>
          <Button onClick={handleCopyState} variant="secondary" size="sm" data-id="element-1919">
            {copied ? <Check className="w-4 h-4 mr-2" data-id="element-1920" /> : <Copy className="w-4 h-4 mr-2" data-id="element-1921" />}
            {copied ? 'Copied!' : 'Copy JSON'}
          </Button>
        </div>
      </Card>

      {/* View Tabs */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-1922">
        <div className="flex gap-1 p-2 bg-slate-50" data-id="element-1923">
          {[{
          id: 'stats',
          label: 'User Stats'
        }, {
          id: 'progress',
          label: 'Module Progress'
        }, {
          id: 'questions',
          label: 'Question States'
        }, {
          id: 'family',
          label: 'Family Mastery'
        }].map(tab => <button key={tab.id} onClick={() => setActiveView(tab.id as any)} className={`
                flex-1 px-4 py-2.5 rounded-xl font-bold text-sm transition-all
                ${activeView === tab.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'}
              `} data-id="element-1924">

              {tab.label}
            </button>)}
        </div>

        {/* Content */}
        <div className="p-6" data-id="element-1925">
          {activeView === 'stats' && <div className="space-y-4" data-id="element-1926">
              <h3 className="font-bold text-slate-900 mb-4 text-lg" data-id="element-1927">
                User Statistics
              </h3>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl overflow-x-auto text-sm font-mono border border-slate-700" data-id="element-1928">
                {JSON.stringify(state.stats, null, 2)}
              </pre>
            </div>}

          {activeView === 'progress' && <div className="space-y-4" data-id="element-1929">
              <h3 className="font-bold text-slate-900 mb-4 text-lg" data-id="element-1930">
                Module Progress
              </h3>
              <div className="space-y-3" data-id="element-1931">
                {Object.entries(state.progress).map(([moduleId, progress]) => <div key={moduleId} className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-1932">

                    <div className="flex items-center justify-between mb-2" data-id="element-1933">
                      <span className="font-bold text-slate-900" data-id="element-1934">
                        {moduleId}
                      </span>
                    </div>
                    <div className="text-xs text-slate-600 space-y-1" data-id="element-1935">
                      <div data-id="element-1936">
                        Cheat sheet viewed:{' '}
                        {progress.cheatSheetViewed ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}

          {activeView === 'questions' && <div className="space-y-4" data-id="element-1937">
              <h3 className="font-bold text-slate-900 mb-4 text-lg" data-id="element-1938">
                Question States ({Object.keys(state.questionStates).length})
              </h3>
              <div className="max-h-96 overflow-y-auto space-y-2" data-id="element-1939">
                {Object.entries(state.questionStates).slice(0, 50).map(([qId, qState]) => <div key={qId} className="bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-1940">

                      <div className="flex items-center justify-between" data-id="element-1941">
                        <span className="font-mono text-slate-700 text-sm font-medium" data-id="element-1942">
                          {qId}
                        </span>
                        <div className="flex gap-2 items-center" data-id="element-1943">
                          <Badge variant={qState.status === 'mastered' ? 'success' : 'neutral'} size="sm" data-id="element-1944">

                            {qState.status}
                          </Badge>
                          <span className="text-slate-500 text-xs" data-id="element-1945">
                            {qState.attempts} attempts
                          </span>
                        </div>
                      </div>
                    </div>)}
                {Object.keys(state.questionStates).length > 50 && <div className="text-center text-slate-400 text-xs py-2" data-id="element-1946">
                    ... and {Object.keys(state.questionStates).length - 50} more
                  </div>}
              </div>
            </div>}

          {activeView === 'family' && <div className="space-y-4" data-id="element-1947">
              <h3 className="font-bold text-slate-900 mb-4 text-lg" data-id="element-1948">
                Family Mastery (
                {state.familyMastery ? Object.keys(state.familyMastery).length : 0}
                )
              </h3>
              {state.familyMastery && <div className="max-h-96 overflow-y-auto space-y-2" data-id="element-1949">
                  {Object.entries(state.familyMastery).slice(0, 50).map(([familyId, mastery]) => <div key={familyId} className="bg-slate-50 p-3 rounded-xl border border-slate-100" data-id="element-1950">

                        <div className="flex items-center justify-between" data-id="element-1951">
                          <span className="font-mono text-slate-700 text-sm font-medium" data-id="element-1952">
                            {familyId}
                          </span>
                          <div className="flex gap-2 items-center" data-id="element-1953">
                            <span className="text-slate-500 text-xs" data-id="element-1954">
                              {mastery.attempts} attempts
                            </span>
                            <Badge variant={mastery.masteryScore >= 70 ? 'success' : 'warning'} size="sm" data-id="element-1955">

                              {Math.round(mastery.masteryScore)}%
                            </Badge>
                          </div>
                        </div>
                      </div>)}
                </div>}
            </div>}
        </div>
      </div>
    </div>;
}
function StatBox({
  label,
  value,
  color
}: {
  label: string;
  value: string | number;
  color: string;
}) {
  const colorClasses = {
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    rose: 'bg-rose-50 text-rose-600 border-rose-200'
  };
  return <div className={`p-4 rounded-xl border-2 shadow-sm ${colorClasses[color as keyof typeof colorClasses]}`} data-id="element-1956">

      <div className="text-xs font-bold uppercase tracking-wider opacity-70 mb-1.5" data-id="element-1957">
        {label}
      </div>
      <div className="text-2xl font-bold" data-id="element-1958">{value}</div>
    </div>;
}